
/* eslint-disable vue/no-mutating-props */
// `target` is the checkup page's shared form-model object (mixins/checkup-page.js
// pattern, matching components/schedule/v2/steps/*), passed by reference so this
// step can push/splice its drugs list and flip no_drugs in place by design.
import _ from 'lodash'
import GuidanceCard from '../parts/GuidanceCard.vue'
import SelectedItem from '../parts/SelectedItem.vue'
import MedDetailPanel from '../parts/MedDetailPanel.vue'
import RefillPicker from '../parts/RefillPicker.vue'
import ManualDrugMatches from '../parts/ManualDrugMatches.vue'
import { defaultRefill, refillToStored, refillFromStored, refillLabel, unitWordForForm, daysSupplyLabel, daysSupplyFromFrequency, dosageStrength } from '../parts/medications'
import { dosageKey } from '~/util/drugs'

// Manual-add "Form" choices — ported verbatim from the MedicareCheckup
// platform's StepMedications.vue FORM_OPTIONS. Display-only: folded into
// custom_dosage (see manualCustomDosage() below), never a stored field of
// its own — flow's Drug.dosage (flow/consults/serializers.py:25-31) has no
// optional fields, so a manual entry (no rxcui, no strength_units, no
// has_duplicate_name) can never populate a valid partial DrugDosage.
const FORM_OPTIONS = [
    'Tablet / Pill',
    'Capsule',
    'Liquid / Solution',
    'Injection',
    'Inhaler',
    'Cream / Ointment',
    'Patch',
    'Other',
]

function freshManual () {
    return { name: '', strength: '', form: FORM_OPTIONS[0], refill: defaultRefill() }
}

// "Did you mean…?" resolution of a hand-typed medication name (see
// parts/ManualDrugMatches.vue for why this matters: only drugs with an rxcui
// reach Oracle when quoting). Same 2-char floor and 300ms debounce as
// GuidanceCard's own search box, so typing feels identical in both places.
const MIN_MATCH_LENGTH = 2
const MATCH_DEBOUNCE_MS = 300
// The manual form is a form, not a results list — a handful of candidates is
// enough to recognise the right one, and more would bury the fields below it.
const MAX_MATCHES = 5

export default {
    components: { GuidanceCard, SelectedItem, MedDetailPanel, RefillPicker, ManualDrugMatches },
    props: {
        target: { type: Object, required: true },
        // Copy is a prop so /renewal/* can reframe this step for a
        // returning client ("Are these still your…") while /checkup/*
        // keeps its first-visit wording (#656). The defaults are the
        // checkup strings verbatim, so every existing caller renders
        // byte-identical output without passing anything.
        heading: { type: String, default: 'What medications do you take?' },
        lede: { type: String, default: 'Part D plans cover the same drug at very different prices. Your list helps us find the plans that cover your prescriptions at the lowest cost.' },
    },
    data () {
        return {
            results: [],
            searching: false,
            // Monotonic guard against out-of-order search responses — same
            // rationale as DoctorsStep.searchSeq.
            searchSeq: 0,
            showInfusionNote: false,
            // Add flow: which result row (by index into `results`) has its
            // detail panel open, and the getDrugOptions response for it.
            openIndex: null,
            openOptions: null,
            loadingOptions: false,
            manual: freshManual(),
            formOptions: FORM_OPTIONS,
            // "Did you mean…?" state for the manual ADD form: candidate hits
            // for the typed name, and — once one is picked — that drug plus its
            // getDrugOptions response, which hand the manual form over to
            // MedDetailPanel so the entry lands with a real rxcui.
            manualMatches: [],
            manualMatchSeq: 0,
            manualPick: null,
            manualPickOptions: null,
            manualPickLoading: false,
            // Edit flow (see startEdit() below for why this is a from-scratch
            // reselection rather than a state-restoring pre-fill).
            editIndex: null,
            editOptions: null,
            editManual: null,
            editLoading: false,
            // The same resolution offer on the edit card, for the manual rows
            // already sitting on a client's list. Its own seq/list so an open
            // add form and an open edit card can never cancel each other.
            editMatches: [],
            editMatchSeq: 0,
            editPick: null,
        }
    },
    computed: {
        year () { return this.$store.getters.year },
        // Clients without a group have no groupSlug getter value at all —
        // handled the same way form/Drugs.vue does (`groupSlug || ...`),
        // just with no route-param fallback since checkup has no group route.
        groupSlug () { return this.$store.getters.groupSlug || null },
        editDrug () {
            if (this.editIndex === null) { return null }
            const d = this.target.drugs[this.editIndex]
            return d ? { rxcui: d.rxcui, name: d.name, is_generic: d.is_generic } : null
        },
        // Whose dosage options the edit card's MedDetailPanel is configuring:
        // the row being edited when it's a searched drug, or the suggestion the
        // client just picked when it's a manual one being resolved. Gated on
        // rxcui because only a drug with one has options to fetch — a manual row
        // that hasn't been resolved falls through to the manual fields instead.
        editTarget () {
            if (this.editPick) { return this.editPick }
            return this.editDrug && this.editDrug.rxcui ? this.editDrug : null
        },
        // rxcuis already on the list, for ManualDrugMatches' duplicate guard.
        addedRxcuis () {
            return (this.target.drugs || []).map(d => d.rxcui).filter(Boolean)
        },
        // The stored refill answer for the row being edited — the refill
        // format (#656) persists quantity/frequency/as_needed/excluded
        // losslessly, so the edit card opens on what the client actually
        // said instead of resetting to defaults like the old derived model.
        editInitialRefill () {
            const d = this.editIndex !== null ? this.target.drugs[this.editIndex] : null
            return d ? refillFromStored(d) : null
        },
    },
    created () {
        // Per-instance debounces (an options-level _.debounce would be shared
        // across every mounted MedicationsStep) — same rationale as
        // GuidanceCard.vue's created() hook.
        this.debouncedManualMatch = _.debounce(() => this.findMatches('manual'), MATCH_DEBOUNCE_MS)
        this.debouncedEditMatch = _.debounce(() => this.findMatches('edit'), MATCH_DEBOUNCE_MS)
    },
    methods: {
        async onSearch (query) {
            const seq = ++this.searchSeq
            this.closeDetail()
            if (!query) { this.results = []; this.searching = false; return }
            this.searching = true
            const resp = await this.$api.searchDrugs(
                { query, year: this.year, group_slug: this.groupSlug },
                { spinner: false },
            ).catch(() => null)
            if (seq !== this.searchSeq) { return } // stale — a newer search owns the state
            this.searching = false
            this.results = resp?.results || []
        },
        isAdded (r) { return (this.target.drugs || []).some(d => d.rxcui === r.rxcui) },
        resultMeta (r) {
            if (r.generic && r.generic.name) { return r.generic.name }
            if (r.is_generic) { return 'Generic' }
            return null
        },
        async openDetail (r, index, { again = false } = {}) {
            // Defensive twin of the template's click guard. `again` is the
            // explicit "Add again" path — the client is telling us they take a
            // second strength of this drug, so the rxcui check doesn't apply.
            // addFromResult() still refuses an identical dosage.
            if (!again && this.isAdded(r)) { return }
            this.openIndex = index
            this.openOptions = null
            this.loadingOptions = true
            const resp = await this.$api.getDrugOptions(
                { year: this.year, rxcui: r.rxcui, group_slug: this.groupSlug },
                { spinner: false },
            ).catch(() => null)
            // Stale-response guard: if the user opened a different result (or
            // closed the panel) while this fetch was in flight, dropping the
            // response prevents drug A's dosage options from filling drug B's
            // open panel — which could persist B with A's dosage rxcui.
            if (this.openIndex !== index) { return }
            this.loadingOptions = false
            this.openOptions = resp || { dosages: [] }
        },
        closeDetail () {
            this.openIndex = null
            this.openOptions = null
            this.loadingOptions = false
        },
        // Whitelisted to match the Drug serializer exactly (flow/consults/
        // serializers.py:34-44). MedDetailPanel's emitted `dosage` is a raw
        // getDrugOptions entry, which — per the brief — carries a `packages`
        // array that DrugDosage doesn't declare. DrugForm.vue/form/Drugs.vue
        // (components/DrugForm.vue:60-110, components/form/Drugs.vue:270-310)
        // let pydantic silently drop that extra field server-side rather than
        // stripping it client-side; we strip it explicitly here so the object
        // this step holds already matches what's actually persisted.
        assembleDrug (hit, payload) {
            const dosage = payload.dosage
                ? {
                    rxcui: payload.dosage.rxcui,
                    form: payload.dosage.form,
                    strength: payload.dosage.strength,
                    strength_units: payload.dosage.strength_units,
                    name: payload.dosage.name,
                    has_duplicate_name: payload.dosage.has_duplicate_name,
                }
                : null
            return {
                rxcui: hit.rxcui || null,
                name: hit.name,
                is_generic: !!hit.is_generic,
                dosage,
                custom_dosage: payload.custom_dosage || null,
                package: payload.package || null,
                quantity: payload.quantity,
                frequency: payload.frequency,
                // The refill format (#656): the as-needed flag, and the
                // rarely/keep-on-file option as the pre-existing excluded
                // flag — exactly the field the quote path already filters
                // out before Oracle. Both explicit booleans so an edit OFF
                // either state clears it.
                as_needed: payload.as_needed,
                excluded: payload.excluded,
            }
        },
        addFromResult (r, payload) {
            const drug = this.assembleDrug(r, payload)
            // The duplicate worth refusing is the same STRENGTH twice, which
            // double-counts in the Part D estimate. Two different strengths of
            // one drug are a real prescription, so they're allowed — identity
            // is the dosage signature, not the rxcui, because Oracle returns
            // distinct strengths under one rxcui (util/drugs.js's dosageKey).
            const key = dosageKey(drug.dosage)
            if (key && (this.target.drugs || []).some(d => dosageKey(d.dosage) === key)) {
                this.$warning('You already added that strength. Pick a different one to add it alongside.')
                return
            }
            // Reassign rather than push() — target.drugs is proxied through
            // to the page's reactive this.form.drugs, and Vue's deep watcher
            // in mixins/survey-form.js hands the SAME array reference as
            // both newValue/oldValue on an in-place mutation, so its
            // `_.isEqual(newValue, oldValue)` guard would silently swallow
            // the change and nothing would autosave.
            this.target.drugs = [...this.target.drugs, drug]
            this.setOptOut(false)
            this.results = []
            this.closeDetail()
            this.$refs.card.clearSearch()
        },
        // The counting word for the refill row ("30 tablets, …"), off the
        // manual form's Form pick or a dosage's Oracle form string.
        unitFor (form) { return unitWordForForm(form) },
        // The Drug serializer (flow/consults/serializers.py:34-44) requires
        // `quantity` (conint ge=1) and `frequency` on every item — unlike
        // Provider/Hospital, there is no ManualDrug union variant to fall
        // back on. Without these two fields the autosave PATCH 422s and the
        // entry silently never persists. Both now come straight off the
        // refill answer (refillToStored — #656's refill format), along with
        // as_needed and the rarely/keep-on-file option as `excluded`.
        //
        // custom_dosage is the ONLY place a manual entry's strength survives:
        // flow's Drug.dosage (serializers.py:25-31) has no optional fields,
        // so a manual entry can never populate a valid partial DrugDosage to
        // carry strength structurally. `m.form` is deliberately NOT folded in
        // (platform parity — its medSub() never showed form either).
        assembleManualDrug (m) {
            return {
                name: m.name.trim(),
                custom_dosage: [m.strength.trim(), refillLabel(m.refill, this.unitFor(m.form))]
                    .filter(Boolean).join(' · '),
                ...refillToStored(m.refill),
            }
        },
        // Look up whatever name is currently typed in the manual add form
        // ('manual') or the edit card ('edit') and offer the hits as "did you
        // mean…?" suggestions. Debounced per form — see created().
        //
        // Deliberately silent on failure: a network error or an Oracle outage
        // leaves the suggestion strip empty, which is exactly the pre-existing
        // behavior (type the details by hand). Surfacing an error here would
        // block a form the client only reached BECAUSE search wasn't working.
        async findMatches (which) {
            const isEdit = which === 'edit'
            const form = isEdit ? this.editManual : this.manual
            const seqKey = isEdit ? 'editMatchSeq' : 'manualMatchSeq'
            const listKey = isEdit ? 'editMatches' : 'manualMatches'
            const name = ((form && form.name) || '').trim()
            const seq = ++this[seqKey]
            if (name.length < MIN_MATCH_LENGTH) { this[listKey] = []; return }
            const resp = await this.$api.searchDrugs(
                { query: name, year: this.year, group_slug: this.groupSlug },
                { spinner: false },
            ).catch(() => null)
            // Stale-response guard — same rationale as onSearch()/openDetail():
            // a slower earlier lookup must not repopulate the strip with
            // candidates for a name the client has since changed.
            if (seq !== this[seqKey]) { return }
            this[listKey] = (resp?.results || []).slice(0, MAX_MATCHES)
        },
        // Add form: the client accepted a suggestion, so fetch its real dosage
        // options and hand the form over to MedDetailPanel.
        async pickManualMatch (r) {
            this.manualPick = r
            this.manualPickOptions = null
            this.manualPickLoading = true
            const resp = await this.$api.getDrugOptions(
                { year: this.year, rxcui: r.rxcui, group_slug: this.groupSlug },
                { spinner: false },
            ).catch(() => null)
            // Same stale guard as openDetail(): if the client backed out or
            // picked a different suggestion mid-fetch, drop this response rather
            // than filling drug B's panel with drug A's dosages.
            if (this.manualPick !== r) { return }
            this.manualPickLoading = false
            this.manualPickOptions = resp || { dosages: [] }
        },
        // Back out of a pick to the manual fields (the panel's own X), keeping
        // the form open and whatever was typed — not the same thing as
        // abandoning the manual add altogether.
        clearManualPick () {
            this.manualPick = null
            this.manualPickOptions = null
            this.manualPickLoading = false
        },
        // A resolved manual entry is stored through assembleDrug(), exactly like
        // one added from the search dropdown: real rxcui, real dosage, real
        // package — so it reaches Oracle and actually affects the quote.
        addFromManualPick (payload, close) {
            this.target.drugs = [...this.target.drugs, this.assembleDrug(this.manualPick, payload)]
            this.resetManualForm()
            this.setOptOut(false)
            close()
        },
        resetManualForm () {
            this.manual = freshManual()
            this.manualMatches = []
            this.clearManualPick()
        },
        // Edit card: same offer for a manual row already on the client's list.
        // On confirm, onEditAdd() replaces that row in place with the resolved
        // drug (see editTarget / onEditAdd).
        async pickEditMatch (r) {
            this.editPick = r
            this.editOptions = null
            this.editLoading = true
            const resp = await this.$api.getDrugOptions(
                { year: this.year, rxcui: r.rxcui, group_slug: this.groupSlug },
                { spinner: false },
            ).catch(() => null)
            if (this.editPick !== r) { return }
            this.editLoading = false
            this.editOptions = resp || { dosages: [] }
        },
        // The edit panel's X: back out of a resolution attempt to the manual
        // fields, or cancel the whole edit when there was no pick to undo.
        onEditPanelClose () {
            if (this.editPick) {
                this.editPick = null
                this.editOptions = null
                return
            }
            this.cancelEdit()
        },
        addManual (close) {
            if (!this.manual.name.trim()) { return }
            this.target.drugs = [...this.target.drugs, this.assembleManualDrug(this.manual)]
            this.resetManualForm()
            this.setOptOut(false)
            close()
        },
        remove (i) { this.target.drugs = this.target.drugs.filter((_, idx) => idx !== i) },
        setOptOut (v) { this.$set(this.target, 'no_drugs', v) },
        onClearAndOptOut () {
            this.target.drugs = []
            this.setOptOut(true)
        },
        // Edit flow: the refill answer prefills faithfully (#656's format is
        // stored losslessly — see editInitialRefill), but the DOSAGE still
        // reopens on the defaults: a persisted Drug carries the chosen
        // dosage, yet MedDetailPanel tracks its selection as an index into
        // the re-fetched options list, and matching the stored dosage back
        // to a row is ambiguous when Oracle repeats rxcuis (the omeprazole
        // case). Documented simplification carried over from v1.
        // On add, the existing entry is replaced in place rather than a new
        // one being pushed.
        async startEdit (i) {
            const drug = this.target.drugs[i]
            this.editIndex = i
            this.editManual = null
            this.editOptions = null
            this.editPick = null
            this.editMatches = []
            if (drug && drug.rxcui) {
                this.editLoading = true
                const resp = await this.$api.getDrugOptions(
                    { year: this.year, rxcui: drug.rxcui, group_slug: this.groupSlug },
                    { spinner: false },
                ).catch(() => null)
                // Same stale-response guard as openDetail: bail if the user
                // switched to editing a different drug mid-fetch.
                if (this.editIndex !== i) { return }
                this.editLoading = false
                this.editOptions = resp || { dosages: [] }
                this.revealEditPanel()
            } else {
                // Manually-added drugs have no rxcui/dosage to fetch options
                // for. Name and the refill answer ARE stored, so both
                // prefill; strength/form live only inside the derived
                // custom_dosage string, so those two reopen fresh for the
                // client to restate.
                this.editManual = {
                    ...freshManual(),
                    name: (drug && drug.name) || '',
                    refill: refillFromStored(drug),
                }
                // Look the stored name up straight away rather than waiting for
                // an @input. This card is the ONLY route by which an existing
                // unresolved row gets an rxcui (browser-QA finding), and its
                // name field arrives pre-filled — a client with no reason to
                // retype what's already correct would never see the offer.
                this.findMatches('edit')
                this.revealEditPanel()
            }
        },
        // The panel now opens attached to its own row, so the row is by
        // definition on screen — but the panel itself is tall, and a row near
        // the bottom of the viewport would open most of it below the fold.
        // 'nearest' scrolls only when it actually doesn't fit, so a panel
        // already fully visible never moves. Called once the panel's real
        // content is in place, not while it still reads "Loading…", so the
        // page doesn't jump twice.
        revealEditPanel () {
            this.$nextTick(() => {
                const el = this.$el && this.$el.querySelector('[data-test="med-edit-panel"]')
                // jsdom has no scrollIntoView, and neither does SSR.
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
                }
            })
        },
        cancelEdit () {
            this.editIndex = null
            this.editOptions = null
            this.editManual = null
            this.editLoading = false
            this.editPick = null
            this.editMatches = []
        },
        onEditAdd (payload) {
            // Reassign (map) rather than splice(index, 1, replacement) — see
            // the reassign-not-mutate note on addFromResult() above; the same
            // in-place-mutation-defeats-the-autosave-watcher issue applies to
            // splice() here.
            // editPick when a manual row is being resolved to a real drug (its
            // rxcui/name/is_generic are what must be stored, not the hand-typed
            // row's), otherwise the existing row being reconfigured.
            const hit = this.editPick || this.target.drugs[this.editIndex]
            const editIndex = this.editIndex
            this.target.drugs = this.target.drugs.map((d, idx) => idx === editIndex ? this.assembleDrug(hit, payload) : d)
            this.cancelEdit()
        },
        saveEditManual () {
            if (!this.editManual.name.trim()) { return }
            // Same serializer requirement as addManual() above — quantity +
            // frequency must always be present, not just {name, custom_dosage}.
            const editIndex = this.editIndex
            const replacement = this.assembleManualDrug(this.editManual)
            this.target.drugs = this.target.drugs.map((d, idx) => idx === editIndex ? replacement : d)
            this.cancelEdit()
        },
        // Saved-row bold name: name + strength (platform parity — e.g.
        // "omeprazole 10 mg"), matching the platform's medName() exactly.
        // Manual entries have no separate strength field on Drug (see
        // manualCustomDosage() above), so only searched drugs show it here —
        // theirs is already folded into custom_dosage too, same intentional
        // "shown twice" arrangement the platform itself uses.
        drugName (d) {
            const strength = d.dosage ? dosageStrength(d.dosage) : ''
            return [d.name, strength].filter(Boolean).join(' ')
        },
        // Saved-row sub: the custom_dosage string — [strength, refill phrase]
        // wherever it's built (MedDetailPanel.confirmAdd()/
        // assembleManualDrug() above), e.g. "10 mg · 30 tablets every month".
        //
        // Fallback (#598 feedback): rows saved before this wizard existed — or
        // by an agent through /survey/* — carry dosage/quantity/frequency but
        // no custom_dosage, so they rendered with a bold name and a blank
        // sub-line while wizard-added rows showed a full one. Rebuild what IS
        // stored: strength, and the days-supply recoverable from frequency.
        // The regimen is deliberately NOT reconstructed from quantity —
        // guessing "3 times daily" from a fill size would state something
        // about a client's regimen that nothing actually recorded.
        drugSub (d) {
            if (d.custom_dosage) { return d.custom_dosage }
            return [
                dosageStrength(d.dosage),
                daysSupplyLabel(daysSupplyFromFrequency(d.frequency)),
            ].filter(Boolean).join(' · ') || null
        },
    },
}
